import { publicCatalogApi } from '@/features/publicsite/catalog/api/publicCatalogApi';
import { CatalogSection } from '@/features/publicsite/catalog/CatalogSection';
import { FundingCard } from '@/features/publicsite/catalog/FundingCard';
import { useHashTarget } from '@/features/publicsite/catalog/useHashTarget';
import type { Measure } from '@/features/publicsite/types/funding/Measure';
import { languageFallbackOf, servedLanguage } from '@/features/publicsite/contentLanguage';
import { usePageLanguageFallback } from '@/features/publicsite/usePageLanguageFallback';
import { usePublicList } from '@/features/publicsite/usePublicList';
import { domainLabel, useThematicDomains } from '@/features/common/useThematicDomains';
import { useLanguage } from '@/i18n/LanguageContext';
import { publicCatalog } from '@/i18n/dictionaries/publicCatalog';
import { useTranslation } from '@/i18n/useTranslation';

export function FundingPage() {
  const { t } = useTranslation(publicCatalog);
  const { language } = useLanguage();
  const measures = usePublicList<Measure>(publicCatalogApi.measuresUrl);
  // The same nomenclature the library reads, because funding_measure.domains holds ids into it and
  // the catalogue answers with those ids; the name a reader sees is resolved here, in their own
  // language.
  const domains = useThematicDomains();
  // Same anchors, same gap: nothing links to #masura-{id} yet, and the day something does it has
  // to work rather than be discovered dead.
  useHashTarget(measures !== null);
  // Measure by measure (T234): a measure has an English version when somebody wrote one, so the
  // notice says "partly" for a catalogue where some are translated and some are not, and nothing
  // at all once they all are.
  usePageLanguageFallback(languageFallbackOf((measures ?? []).map(servedLanguage), language));

  const domainOf = (m: Measure) => {
    const id = m.domains?.[0];
    if (!id) return null;
    const item = domains.find((d) => d.id === id);
    return item ? domainLabel(item, language) : null;
  };

  return (
    <CatalogSection
      eyebrow={t('eyebrowCatalog')}
      heading={t('fundingHeading')}
      lead={t('fundingLead')}
      items={measures}
      renderCard={(m) => <FundingCard key={m.id} measure={m} domain={domainOf(m)} />}
    />
  );
}
